import { z } from 'zod';

import { MESSAGE_DATA_TYPES } from './discriminators';

import { isSameUserId } from '@/utils/isSameUserId';

/**
 * A Primum Support message posted into an inquiry conversation by staff,
 * addressed to the submitter, the recipient, or both.
 *
 * Written by the backend's support-message route, never by this app. The
 * composer's own "Send as Primum Support" path writes
 * {@link MESSAGE_DATA_TYPES.PRIMUM_SUPPORT} and carries no audience.
 */

/**
 * Who a support message is addressed to, named for the roles an inquiry has
 * rather than for user types: the submitter is not always an LMP, and the
 * recipient is whoever the inquiry was routed to.
 *
 * `.catch(undefined)` is deliberate: an unrecognised value must degrade to "no
 * audience" rather than fail `safeParse` for the whole attributes object, which
 * would drop every field for that message.
 */
export const supportMessageAudienceSchema = z
  .enum(['submitter', 'recipient', 'both'])
  .optional()
  .catch(undefined);

export type SupportMessageAudience = NonNullable<z.infer<typeof supportMessageAudienceSchema>>;

export const payloadSchema = z.object({
  type: z.literal(MESSAGE_DATA_TYPES.SUPPORT_MESSAGE),
  audience: supportMessageAudienceSchema,
  audienceUserIds: z.array(z.number()).optional(),
});

export type SupportMessageData = z.infer<typeof payloadSchema>;

export const attributesSchema = z.object({
  /**
   * Which participant this support message is meant for.
   *
   * RENDER-TIME ONLY, AND NOT A CONFIDENTIALITY BOUNDARY. Both participants
   * hold a Twilio grant on the conversation and both clients receive every
   * message in it, so the excluded party already has the body — hiding it keeps
   * it out of the UI and nothing more. Never treat a single-audience support
   * message as private from the other participant.
   */
  audience: supportMessageAudienceSchema,
  /**
   * The user ids `audience` resolved to at post time, so the client gate is a
   * membership test against the viewer rather than something needing the
   * inquiry's submitter/recipient roles. Preferred over `audience` when
   * present: it is the backend's own resolved answer.
   */
  audienceUserIds: z.array(z.number()).optional(),
});

/** The audience fields, however they were read — parsed message or raw attributes. */
type SupportMessageAudienceFields = {
  audience?: SupportMessageAudience;
  audienceUserIds?: readonly number[];
};

type SupportMessageViewer = {
  /** The viewing user's id, respecting impersonation (`userIdAtom`). */
  userId: string | number | undefined;
  /**
   * Whether the viewer is the inquiry's submitter rather than its recipient.
   * Omitted where the caller holds a conversation but not an inquiry role — the
   * inbox handlers — in which case the `audience` label alone cannot be
   * resolved and the message passes.
   */
  isSubmitter?: boolean;
};

/**
 * Whether a support message's audience includes this viewer.
 *
 * `audienceUserIds` wins when present, since it is what the backend resolved
 * the audience to for this conversation. `audience` plus the viewer's role is
 * the fallback for a producer that writes only the label.
 *
 * **Fails open.** An absent, empty, or unrecognised audience shows the message
 * to everyone. The gate is cosmetic — the other participant's client has the
 * body regardless — so a support message swallowed by a shape we did not expect
 * is a worse outcome than one shown too widely.
 */
export function isSupportMessageVisibleTo(
  fields: SupportMessageAudienceFields,
  viewer: SupportMessageViewer
): boolean {
  const { audience, audienceUserIds } = fields;

  if (audienceUserIds?.length) {
    return audienceUserIds.some((audienceUserId) => isSameUserId(audienceUserId, viewer.userId));
  }

  if (viewer.isSubmitter === undefined) {
    return true;
  }

  if (audience === 'submitter') {
    return viewer.isSubmitter;
  }

  if (audience === 'recipient') {
    return !viewer.isSubmitter;
  }

  return true;
}

/**
 * Whether the message is addressed to one participant rather than both, which
 * is what earns the support bubble's "Only you" visibility badge.
 *
 * Reads the `audience` label rather than counting `audienceUserIds`: a `both`
 * audience on an inquiry with no recipient yet resolves to a single id, and
 * that is still a message to everyone in the thread, not a one-sided one.
 */
export function isSingleAudienceSupportMessage(
  fields: Pick<SupportMessageAudienceFields, 'audience'>
): boolean {
  return fields.audience === 'submitter' || fields.audience === 'recipient';
}
